//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled from 'styled-components';

import ActionButton from '@components/atoms/action-button/ActionButton';
import { PrimaryLinkButton } from '@components/atoms/button/Button';
import { BodyBoldText } from '@components/atoms/text/Text';
import Cuid from '@models/Cuid';
import { SocialNetwork } from '@models/SocialNetwork';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { PreferredCaptionEditor } from '@uia/details/captions/PreferredCaptionEditor';
import { PreferredCaptionHeader } from '@uia/details/captions/PreferredCaptionHeader';
import { UIACaption } from '@uia/details/captions/UIACaption';
import { isDefined } from '@utils/filterPredicates';
import { getRgbaForThemeHex } from '@utils/getRgbaForThemeHex';

export interface EditableCaptionProps {
  cuid: Cuid;
  originalCaption: string;
  preferredCaption: string;
  socialNetwork: SocialNetwork;
}

export const EditableCaption = ({
  cuid,
  originalCaption,
  preferredCaption,
  socialNetwork,
}: EditableCaptionProps) => {
  const [isEditingCaption, setIsEditingCaption] = useState(false);
  const [isOriginalCaptionExpanded, setIsOriginalCaptionExpanded] =
    useState(false);

  return (
    <>
      {isEditingCaption ? (
        <PreferredCaptionEditor
          caption={preferredCaption}
          cuid={cuid}
          hasPreferredCaption={isDefined(preferredCaption)}
          onCancel={() => setIsEditingCaption(false)}
          onRevert={() => {
            setIsEditingCaption(false);
          }}
          onSave={async () => {
            setIsEditingCaption(false);
          }}
        />
      ) : (
        <div>
          {originalCaption !== preferredCaption && <PreferredCaptionHeader />}
          <CaptionContainer>
            <UIACaption
              caption={preferredCaption}
              socialNetwork={socialNetwork}
            />
            <CaptionActionsContainer>
              <CaptionActionButtons>
                <EditActionButton
                  iconName='edit'
                  label='Customize'
                  onClick={() => setIsEditingCaption(true)}
                  trackingId={UIATrackingIds.DETAILS_EDIT_CAPTION_TRIGGER}
                />
              </CaptionActionButtons>
            </CaptionActionsContainer>
          </CaptionContainer>
          <OriginalCaptionSection>
            {isOriginalCaptionExpanded && (
              <div>
                <FlexSpaceBetween>
                  <BodyBoldText>Original Caption</BodyBoldText>
                  {originalCaption.length > 0 && (
                    <PrimaryLinkButton
                      onClick={() => setIsOriginalCaptionExpanded(false)}
                      trackingId={
                        UIATrackingIds.DETAILS_EDIT_CAPTION_TOGGLE_ORIGINAL
                      }
                    >
                      Collapse
                    </PrimaryLinkButton>
                  )}
                </FlexSpaceBetween>
                {originalCaption && (
                  <UIACaption
                    caption={originalCaption}
                    socialNetwork={socialNetwork}
                  />
                )}
              </div>
            )}
            {originalCaption !== preferredCaption &&
              !isOriginalCaptionExpanded && (
                <PrimaryLinkButton
                  onClick={() => setIsOriginalCaptionExpanded(true)}
                  trackingId={
                    UIATrackingIds.DETAILS_EDIT_CAPTION_TOGGLE_ORIGINAL
                  }
                >
                  Show Original
                </PrimaryLinkButton>
              )}
          </OriginalCaptionSection>
        </div>
      )}
    </>
  );
};

const CaptionContainer = styled.div(
  ({ theme }) => `
    position: relative;
    width: 100%;
    min-height: ${theme.spacings[4]};
    padding-top: ${theme.spacings[2]};
  `,
);

const CaptionActionsContainer = styled.div(
  ({ theme }) => `
    position: absolute;
    top: ${theme.spacings[0]};
    right: -${theme.spacings[2]};
    height: 100%;
    width: 15%;
    background: linear-gradient(
      90deg,
      ${getRgbaForThemeHex(theme.colors.white, 0)} 0%,
      ${getRgbaForThemeHex(theme.colors.white, 0.8)} 30%,
      ${getRgbaForThemeHex(theme.colors.white, 1)} 100%
    );
    transition: right 0.2s ease;
    visibility: hidden;

    ${CaptionContainer}:hover & {
      right: ${theme.spacings[0]};
      visibility: visible;
    }
  `,
);

const EditActionButton = styled(ActionButton)(
  ({ theme }) => `
    button {
      border: none;
      background-color: transparent;

      &:hover {
        background-color: ${getRgbaForThemeHex(theme.colors.black, 0.1)};
      }
    }
  `,
);

const CaptionActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const FlexSpaceBetween = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: ${theme.spacings[2]};
  `,
);

const OriginalCaptionSection = styled.div(
  ({ theme }) => `
    margin-top: ${theme.spacings[4]};
  `,
);
